import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  PoButtonModule,
  PoFieldModule,
  PoLoadingModule,
  PoPageModule,
  PoTooltipModule,
} from '@po-ui/ng-components';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ProductValueQueryService } from '../../shared/service/product-value-query.service';
import { TokenService } from '../../shared/service/token.service';
import { ToasterService } from '../../shared/service/toaster.service';
import { ToasterComponent } from '../../shared/components/toaster/toaster.component';
import { ScannerComponent } from '../../shared/components/scanner/scanner.component';
import { StepBreadcrumbComponent, StepBreadcrumbItem } from '../../shared/components/step-breadcrumb/step-breadcrumb.component';

interface PriceRow {
  tipo: string;
  descricao: string;
  valorUnit: number;
}

interface ProductResult {
  code: string;
  sku: string;
  name: string;
  group: string;
  color: string;
  size: string;
  imageUrl: string;
  prices: PriceRow[];
}

@Component({
  standalone: true,
  selector: 'app-product-value-query',
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    PoPageModule,
    PoFieldModule,
    PoButtonModule,
    PoLoadingModule,
    PoTooltipModule,
    ScannerComponent,
    StepBreadcrumbComponent,
    ToasterComponent,
  ],
  templateUrl: './product-value-query.component.html',
  styleUrl: './product-value-query.component.css',
})
export class ProductValueQueryComponent {
  readonly breadcrumbs: StepBreadcrumbItem[] = [
    { label: 'Home', id: '/home' },
    { label: 'Produto', id: '' },
    { label: 'Consulta Valor', id: '' }
  ];

  readonly breadcrumbActiveIndex = 2;

  barcode: string = '';
  isLoading: boolean = false;
  searched: boolean = false;
  product: ProductResult | null = null;
  notFound: boolean = false;
  errorMessage: string = '';

  constructor(
    private productValueQueryService: ProductValueQueryService,
    private tokenService: TokenService,
    private toasterService: ToasterService,
    private router: Router
  ) {}

  navigateBreadcrumb(event: { item: StepBreadcrumbItem; index: number }): void {
    if (event.item.id) {
      this.router.navigate([event.item.id]);
    }
  }

  search(): void {
    const identifier = this.barcode.trim();
    if (!identifier) return;

    const branchCode = Number(this.tokenService.getBranch());

    this.isLoading = true;
    this.product = null;
    this.notFound = false;
    this.errorMessage = '';
    this.searched = false;

    this.productValueQueryService.getProductPrice(identifier, branchCode).pipe(
      catchError((err: any) => {
        const apiErrors = err?.error;
        const msg = Array.isArray(apiErrors) && apiErrors[0]?.message
          ? apiErrors[0].message
          : 'Erro ao consultar o produto.';
        this.toasterService.triggerAlert(msg, 'error');
        this.errorMessage = msg;
        this.notFound = true;
        this.isLoading = false;
        this.searched = true;
        return of(null);
      })
    ).subscribe((priceData: any) => {
      if (!priceData) return;

      const productCode: number = priceData.productCode ?? 0;

      const valueTypeTranslations: Record<string, string> = {
        'retail': 'Varejo',
        'wholesale': 'Atacado',
        'promotional': 'Promocional',
        'cost': 'Custo',
        'sale': 'Venda',
        'list': 'Tabela',
        'special': 'Especial',
        'minimum': 'Mínimo',
        'maximum': 'Máximo',
        'average': 'Médio',
        'price': 'Preço',
        'price table': 'Tabela de Preços',
        'pricetable': 'Tabela de Preços',
      };

      const prices: PriceRow[] = (priceData.prices ?? []).map((p: any) => {
        const rawType: string = (p.valueType ?? '').toLowerCase().trim();
        const tipo = valueTypeTranslations[rawType] ?? p.valueType ?? 'Preço';
        return {
          tipo,
          descricao: p.valueName ?? '',
          valorUnit: p.promotionalPrice ?? p.price ?? 0,
        };
      });

      const partialProduct: ProductResult = {
        code: priceData.productCode ?? identifier,
        sku: priceData.sku ?? '',
        name: priceData.productName ?? identifier,
        group: '',
        color: priceData.colorName ?? '',
        size: priceData.sizeName ?? '',
        imageUrl: '',
        prices,
      };

      const image$ = productCode
        ? this.productValueQueryService.getProductImages([productCode]).pipe(catchError(() => of(null)))
        : of(null);

      image$.subscribe((imgData: any) => {
        const images = imgData?.items?.[0]?.images ?? [];
        const imageFile: string = images[0]?.imageFile ?? '';
        const imageUrl = imageFile ? `data:image/jpeg;base64,${imageFile}` : '';

        this.product = { ...partialProduct, imageUrl };
        this.searched = true;
        this.notFound = false;
        this.isLoading = false;
      });
    });
  }

  onScannedData(code: string): void {
    this.barcode = code;
    this.search();
  }
}
